import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Home, ChevronRight, Save, X, MapPin, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const LOCATION_TYPES = [
  { value: 'CITY', label: 'City' },
  { value: 'AIRPORT', label: 'Airport' },
  { value: 'SEAPORT', label: 'Seaport' },
  { value: 'ICD', label: 'ICD (Inland Container Depot)' },
  { value: 'CFS', label: 'CFS (Container Freight Station)' },
  { value: 'WAREHOUSE', label: 'Warehouse' },
  { value: 'CUSTOMS_LOCATION', label: 'Customs Location' },
  { value: 'DELIVERY_POINT', label: 'Delivery Point' },
];

const MODE_OPTIONS = ['AIR', 'SEA', 'ROAD', 'RAIL', 'COURIER', 'MULTI'];

const EMPTY_FORM = {
  location_name: '', location_code: '', country: 'India', state: '', city: '',
  address_line_1: '', address_line_2: '', pincode: '',
  location_type: '', mode_supported: [], is_active: true, remarks: ''
};

const AddLocation = () => {
  const navigate = useNavigate();
  const { id: locationId } = useParams();
  const { toast } = useToast();
  const isEditMode = !!locationId;

  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    const fetchLocation = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${backendUrl}/api/locations/${locationId}`, { headers: getAuthHeaders() });
        if (!response.ok) throw new Error('Failed to load location');
        const data = await response.json();
        setFormData({
          location_name: data.location_name || '',
          location_code: data.location_code || '',
          country: data.country || 'India',
          state: data.state || '',
          city: data.city || '',
          address_line_1: data.address_line_1 || '',
          address_line_2: data.address_line_2 || '',
          pincode: data.pincode || '',
          location_type: data.location_type || '',
          mode_supported: data.mode_supported || [],
          is_active: data.is_active ?? true,
          remarks: data.remarks || ''
        });
      } catch (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        navigate('/settings/locations');
      } finally { setLoading(false); }
    };
    fetchLocation();
  }, [isEditMode, locationId, backendUrl, getAuthHeaders, toast, navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleMode = (mode, checked) => {
    setFormData(prev => ({
      ...prev,
      mode_supported: checked
        ? [...prev.mode_supported, mode]
        : prev.mode_supported.filter(m => m !== mode)
    }));
  };

  const handleSave = async () => {
    if (!formData.location_name.trim()) { toast({ title: "Error", description: "Location name is required", variant: "destructive" }); return; }
    if (!formData.country.trim()) { toast({ title: "Error", description: "Country is required", variant: "destructive" }); return; }
    if (!formData.location_type) { toast({ title: "Error", description: "Location type is required", variant: "destructive" }); return; }

    try {
      setSaving(true);
      const url = isEditMode ? `${backendUrl}/api/locations/${locationId}` : `${backendUrl}/api/locations`;
      const method = isEditMode ? 'PUT' : 'POST';
      const response = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(formData) });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save location');
      }
      toast({ title: "Success", description: isEditMode ? "Location updated" : "Location created" });
      navigate('/locations');
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-accent-blue" /></div>;
  }

  return (
    <div className="space-y-6 pb-8" data-testid="add-location-page">
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Home className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/')}>Home</span>
        <ChevronRight className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/settings')}>Settings</span>
        <ChevronRight className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/settings/locations')}>Location Master</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-text-dark font-medium">{isEditMode ? 'Edit Location' : 'Add Location'}</span>
      </div>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-dark font-manrope">{isEditMode ? 'Edit Location' : 'Add Location'}</h1>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/locations')} data-testid="cancel-btn"><X className="w-4 h-4 mr-2" /> Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-location-btn" className="bg-accent-blue hover:bg-accent-blue/90">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Location'}
          </Button>
        </div>
      </div>

      {/* Location Details */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-blue px-6 py-4">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-accent-blue" />
            <h2 className="text-lg font-semibold text-text-dark font-manrope">LOCATION DETAILS</h2>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Location Name <span className="text-red-500">*</span></Label>
              <Input data-testid="location-name-input" value={formData.location_name} onChange={e => handleChange('location_name', e.target.value)} placeholder="e.g., Mumbai Airport" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Location Code</Label>
              <Input data-testid="location-code-input" value={formData.location_code} onChange={e => handleChange('location_code', e.target.value.toUpperCase())} placeholder="e.g., BOM" className="border-gray-200 font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Location Type <span className="text-red-500">*</span></Label>
              <Select value={formData.location_type || "none"} onValueChange={v => handleChange('location_type', v === "none" ? '' : v)}>
                <SelectTrigger data-testid="location-type-select" className="border-gray-200"><SelectValue placeholder="Select Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select Type --</SelectItem>
                  {LOCATION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Country <span className="text-red-500">*</span></Label>
              <Input data-testid="country-input" value={formData.country} onChange={e => handleChange('country', e.target.value)} placeholder="India" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">State</Label>
              <Input data-testid="state-input" value={formData.state} onChange={e => handleChange('state', e.target.value)} placeholder="e.g., Maharashtra" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">City</Label>
              <Input data-testid="city-input" value={formData.city} onChange={e => handleChange('city', e.target.value)} placeholder="e.g., Mumbai" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Pincode</Label>
              <Input data-testid="pincode-input" value={formData.pincode} onChange={e => handleChange('pincode', e.target.value)} placeholder="e.g., 400001" className="border-gray-200" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Address Line 1</Label>
              <Input data-testid="address1-input" value={formData.address_line_1} onChange={e => handleChange('address_line_1', e.target.value)} placeholder="Street address" className="border-gray-200" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Address Line 2</Label>
              <Input data-testid="address2-input" value={formData.address_line_2} onChange={e => handleChange('address_line_2', e.target.value)} placeholder="Area / Landmark" className="border-gray-200" />
            </div>
          </div>

          {/* Mode Supported */}
          <div className="space-y-2">
            <Label className="text-text-dark font-ibm font-medium text-sm">Mode Supported</Label>
            <div className="flex flex-wrap gap-4" data-testid="mode-supported-group">
              {MODE_OPTIONS.map(mode => (
                <div key={mode} className="flex items-center gap-2">
                  <Checkbox
                    id={`mode-${mode}`}
                    checked={formData.mode_supported.includes(mode)}
                    onCheckedChange={checked => toggleMode(mode, checked)}
                  />
                  <Label htmlFor={`mode-${mode}`} className="text-sm font-ibm cursor-pointer">{mode}</Label>
                </div>
              ))}
            </div>
          </div>

          {/* Remarks + Active toggle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-text-dark font-ibm font-medium text-sm">Remarks</Label>
              <Textarea data-testid="remarks-input" value={formData.remarks} onChange={e => handleChange('remarks', e.target.value)} placeholder="Optional notes..." className="border-gray-200 min-h-[80px]" />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch data-testid="active-toggle" checked={formData.is_active} onCheckedChange={v => handleChange('is_active', v)} />
              <Label className="text-sm font-ibm">{formData.is_active ? 'Active' : 'Inactive'}</Label>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AddLocation;
